import { useEffect, useState } from 'react';
import { usePatientView } from '../App';
import type { Route } from '../lib/routes';
import type { AppSession, MeView, PatientView } from '../lib/types';
import type { AuthConfig } from '../lib/home';
import { goTo, markWelcomed, tabHash, type PatientTab } from '../lib/routes';
import { displayName } from '../lib/session';
import { AppMark, Icon } from '../components/Mark';
import { Toast } from '../components/Bits';
import { UserMenu } from '../components/UserMenu';
import { Onboarding } from './patient/Onboarding';
import { HomeTab } from './patient/HomeTab';
import { HealthTab } from './patient/HealthTab';
import { CoachTab } from './patient/CoachTab';
import { ProfileTab } from './patient/ProfileTab';
import { TemperatureFlow } from './patient/TemperatureFlow';
import { SymptomFlow } from './patient/SymptomFlow';
import { VitalLogFlow } from './patient/VitalLogFlow';
import { DevicesPage } from './patient/DevicesPage';
import { MessagesPage } from './patient/MessagesPage';

const NAV: Array<{ tab: PatientTab; label: string; icon: 'home' | 'health' | 'coach' | 'profile' }> = [
  { tab: 'home', label: 'Home', icon: 'home' },
  { tab: 'health', label: 'Health', icon: 'health' },
  { tab: 'coach', label: 'Coach', icon: 'coach' },
  { tab: 'profile', label: 'Profile', icon: 'profile' },
];

/**
 * The patient app: a sticky top bar with navigation, and a centred content shell whose pages lay
 * themselves out across the width they are given. Logging a temperature is a modal over the page
 * behind it rather than a screen of its own, so it is never more than one tap away.
 */
export function PatientApp({ session, me, config, route, welcome, onSignOut, notice, clearNotice }: { session: AppSession; me: MeView; config: AuthConfig | null; route: Route; welcome: boolean; onSignOut: () => void; notice: string | null; clearNotice: () => void }) {
  const { view, error, reload } = usePatientView(session.token);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (notice) {
      setToast(notice);
      clearNotice();
    }
  }, [notice, clearNotice]);

  if (welcome) {
    return (
      <Onboarding
        view={view}
        onDone={() => {
          markWelcomed(session.personId);
          goTo(tabHash('home'));
        }}
      />
    );
  }

  const say = (s: string) => setToast(s);
  const tab: PatientTab = route.page === 'tab' ? route.tab : 'home';

  let body: JSX.Element;
  if (!view) {
    body = <div className="loading">{error ? <p className="form-error">{error}</p> : <p>Loading…</p>}</div>;
  } else if (route.page === 'inbox') {
    body = <MessagesPage session={session} onChanged={reload} />;
  } else if (route.page === 'devices') {
    body = <DevicesPage session={session} onChanged={reload} />;
  } else {
    body = renderTab(tab, { session, me, config, view, reload, say, onSignOut });
  }

  return (
    <div className="app">
      <header className="topbar">
        <div className="topbar-inner">
          <a className="brand" href={tabHash('home')}>
            <AppMark size={24} />
            <span>OncoCoach</span>
          </a>
          <nav className="topnav" aria-label="Sections">
            {NAV.map((n) => (
              <a key={n.tab} href={tabHash(n.tab)} className={tab === n.tab && route.page !== 'inbox' ? 'on' : ''} aria-current={tab === n.tab ? 'page' : undefined}>
                <Icon name={n.icon} size={17} />
                {n.label}
              </a>
            ))}
          </nav>
          <div className="topbar-right">
            {/* The one number worth carrying on every screen, on the tracks where it decides the day. */}
            {view && view.track.feverIsEmergency ? (
              <a className="pill pill-soft" href="#/temp/log" title="Log your temperature">
                <Icon name="temperature" size={15} /> {view.latestTemperature ? `${view.latestTemperature.celsius.toFixed(1)}°` : 'Log temp'}
              </a>
            ) : null}
            <a className="icon-btn bell" href="#/inbox" aria-label={view?.inboxUnread ? `${view.inboxUnread} unread messages` : 'Messages'}>
              <Icon name="bell" size={19} />
              {view && view.inboxUnread > 0 ? <span className="count">{view.inboxUnread}</span> : null}
            </a>
            <UserMenu name={view?.profile.name ?? displayName(session)} label={displayName(session)} onSignOut={onSignOut} />
          </div>
        </div>
      </header>

      <main className="shell">{body}</main>

      {route.page === 'temp-log' && view ? <TemperatureFlow session={session} view={view} onDone={reload} onClose={() => goTo(tabHash('home'))} /> : null}
      {route.page === 'symptoms' && view ? <SymptomFlow session={session} view={view} onDone={reload} onClose={() => goTo(tabHash('home'))} /> : null}
      {route.page === 'vital-log' && view ? <VitalLogFlow session={session} view={view} kind={route.kind} onDone={reload} onClose={() => goTo(tabHash('home'))} /> : null}
      <Toast text={toast} onClose={() => setToast(null)} />
    </div>
  );
}

function renderTab(tab: PatientTab, p: { session: AppSession; me: MeView; config: AuthConfig | null; view: PatientView; reload: () => Promise<void>; say: (s: string) => void; onSignOut: () => void }): JSX.Element {
  switch (tab) {
    case 'home':
      return <HomeTab {...p} />;
    case 'health':
      return <HealthTab view={p.view} />;
    case 'coach':
      return <CoachTab session={p.session} view={p.view} reload={p.reload} say={p.say} />;
    case 'profile':
      return <ProfileTab session={p.session} me={p.me} config={p.config} view={p.view} onSignOut={p.onSignOut} reload={p.reload} />;
  }
}
